/**
 * @file HTTP client and types for the Players / Teams dashboard backend:
 * summary cards, leaderboard, and activity table (plus Jest mocks).
 */
import { config } from '../config/config';
import type {
  DashboardLeaderboardEntry,
  DashboardLeaderboardMetricId,
  DashboardLeaderboardResponse,
  DashboardLeaderboardSortMode,
  DashboardActivityRow,
  DashboardActivityTableResponse,
  DashboardActivityTableSortColumn,
  DashboardActivityTableSortDirection,
  DashboardTableFilterParams,
} from '../../interfaces/dashboardLeaderboardActivity';
import {
  createMockActivityRow,
  sortDashboardActivityRows,
} from '../helpers/dashboardActivityTable';
import {
  ACTIVITY_TABLE_SORT_UI_TO_API,
  mapActivityTableApiRow,
  mapPlayersGroupToActivityTablePosition,
} from '../helpers/activityTableApi';

export type DashboardMetricSummary = {
  avgGain: number;
  totalGain: number;
  improvedPercentage: number;
  teamAverage: number;
  playerCount: number;
  improvedCount: number;
};

export type DashboardTotalWithBreakdown = {
  totalExams?: number;
  totalSessions?: number;
  breakdown: {
    fresh?: number;
    post?: number;
    armPrimer?: number;
    strength?: number;
    hybrid?: number;
    offDay?: number;
  };
};

export type DashboardMetricsResponse = {
  armScore: DashboardMetricSummary;
  totalArmStrength: DashboardMetricSummary;
  testing: DashboardTotalWithBreakdown;
  training: DashboardTotalWithBreakdown;
};

type DashboardMetricsParams = {
  teamId?: string | null;
  rangePreset?: string;
  playersGroup?: string;
  search?: string;
  startDate?: string | null;
  endDate?: string | null;
};

export type PlayerSummaryCardsData = {
  armScore: { totalGain: number | null; currentValue: number | null };
  totalArmStrength: { totalGain: number | null; currentValue: number | null };
  testing: {
    total: number;
    breakdown: { fresh: number; post: number; armPrimer: number };
    streak: { weeks: number; requirement: string };
  };
  training: {
    total: number;
    breakdown: { strength: number; hybrid: number; offDay: number };
    streak: { weeks: number; requirement: string };
  };
};

export type AlertValue = 'Normal' | 'No Alerts' | 'Watch' | 'Warning' | 'Medical' | number | null;

export type PlayerWeeklyDayData = {
  armFeels: number | null;
  bodyFeels: number | null;
  sleepQuality: number | null;
  armPrimer: 'Completed' | null;
  freshExam: 'Completed' | null;
  postExam: 'Completed' | null;
  strengthAlerts: AlertValue;
  recoveryAlerts: AlertValue;
  fatigueAlerts: AlertValue;
  throwingType: string | null;
  rpe: number | null;
  count: number | null;
  customTraining: 'Completed' | null;
};

export type PlayerWeeklyActivityData = {
  weekStart: string;
  weekEnd: string;
  days: Record<'MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN', PlayerWeeklyDayData>;
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Special team id used only in mocks/tests to force a fully "empty" dashboard.
 * When selected, all dashboard surfaces (summary metrics, charts, leaderboard,
 * and activity table) return no data so the UI can render consistent empty states.
 */
export const NO_DATA_TEAM_ID = 'no-data-team';

const MOCK_API_DELAY_MS = 700;
const inJest = typeof process !== 'undefined' && process.env.JEST_WORKER_ID != null;

/** Returns a Promise that resolves after `ms` milliseconds (mock network delay outside Jest). */
function sleep(ms: number) {
  return new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });
}

/**
 * Stable hash from dashboard filter params so mocks return deterministic series/counts
 * for the same filters.
 */
function hashDashboardParams(params?: DashboardMetricsParams): number {
  const s = JSON.stringify({
    teamId: params?.teamId ?? '',
    rangePreset: params?.rangePreset ?? '',
    playersGroup: params?.playersGroup ?? '',
    search: params?.search ?? '',
    startDate: params?.startDate ?? '',
    endDate: params?.endDate ?? '',
  });
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Dashboard API: summary cards (`getMetrics`), leaderboard (`getLeaderboard`), activity table (`getActivityTable`).
 * Pair with React Query hooks; use `*-Mock` methods only in tests.
 */
export default class DashboardApi {
  private url: string = config.armcareNewServerUrl;

  /** Reads `access_token` from `localStorage` user JSON (same pattern as other portal APIs). */
  private getAuthToken() {
    const rawUser = typeof window !== 'undefined' ? window.localStorage.getItem('user') : null;
    return rawUser ? (JSON.parse(rawUser) as any)?.access_token : null;
  }

  /**
   * Maps UI range preset to backend `rangeKey` query value.
   * @returns `ALL` for “all”, preset id for named ranges, or `null` when custom (caller sends start/end dates).
   */
  private mapSummaryRangeKey(rangePreset?: string) {
    if (!rangePreset || rangePreset === 'custom') return null;
    return rangePreset === 'all' ? 'ALL' : rangePreset;
  }

  private normalizeSummaryData(raw: any): DashboardMetricsResponse {
    const toNumber = (v: unknown) => (typeof v === 'number' ? v : 0);

    const armScore = raw?.armScore ?? {};
    const totalArmStrength = raw?.totalArmStrength ?? {};
    const testing = raw?.testing ?? {};
    const training = raw?.training ?? {};

    return {
      armScore: {
        avgGain: toNumber(armScore.avgGain),
        totalGain: toNumber(armScore.totalGain),
        improvedPercentage: toNumber(armScore.improvedPercentage),
        teamAverage: toNumber(armScore.teamAverage),
        playerCount: toNumber(armScore.playerCount),
        improvedCount: toNumber(armScore.improvedCount),
      },
      totalArmStrength: {
        avgGain: toNumber(totalArmStrength.avgGain),
        totalGain: toNumber(totalArmStrength.totalGain),
        improvedPercentage: toNumber(totalArmStrength.improvedPercentage),
        teamAverage: toNumber(totalArmStrength.teamAverage),
        playerCount: toNumber(totalArmStrength.playerCount),
        improvedCount: toNumber(totalArmStrength.improvedCount),
      },
      testing: {
        totalExams: toNumber(testing.totalExams),
        breakdown: {
          fresh: toNumber(testing?.breakdown?.fresh),
          post: toNumber(testing?.breakdown?.post),
          armPrimer: toNumber(testing?.breakdown?.armPrimer),
        },
      },
      training: {
        totalSessions: toNumber(training.totalSessions),
        breakdown: {
          strength: toNumber(training?.breakdown?.strength),
          hybrid: toNumber(training?.breakdown?.hybrid),
          offDay: toNumber(training?.breakdown?.offDay),
        },
      },
    };
  }

  /**
   * Mock summary metrics for tests and local preview: synthetic trends, testing/training breakdowns.
   * Honors {@link NO_DATA_TEAM_ID} with empty data. Seeded from {@link hashDashboardParams}.
   */
  public async getMetricsMock(
    params?: DashboardMetricsParams,
  ): Promise<{ status: boolean; data: DashboardMetricsResponse }> {
    if (!inJest) await sleep(MOCK_API_DELAY_MS);

    // Deterministic empty snapshot for tests / "no data" scenarios.
    if (params?.teamId === NO_DATA_TEAM_ID) {
      return {
        status: true,
        data: {
          armScore: { avgGain: 0, totalGain: 0, improvedPercentage: 0, teamAverage: 0, playerCount: 0, improvedCount: 0 },
          totalArmStrength: { avgGain: 0, totalGain: 0, improvedPercentage: 0, teamAverage: 0, playerCount: 0, improvedCount: 0 },
          testing: {
            totalExams: 0,
            breakdown: { fresh: 0, post: 0, armPrimer: 0 },
          },
          training: {
            totalSessions: 0,
            breakdown: { strength: 0, hybrid: 0, offDay: 0 },
          },
        },
      };
    }

    const h = hashDashboardParams(params);

    /** Picks a deterministic integer in `[min, max]` seeded from `h`. */
    const seededInt = (min: number, max: number, salt: number) => {
      const v = Math.abs((Math.imul(h, salt) + 12345) | 0);
      return min + (v % (max - min + 1));
    };

    const fresh = seededInt(200, 400, 3);
    const post = seededInt(150, 300, 7);
    const armPrimer = seededInt(100, 300, 11);
    const strength = seededInt(300, 500, 13);
    const hybrid = seededInt(150, 300, 17);
    const offDay = seededInt(200, 400, 19);

    const data: DashboardMetricsResponse = {
      armScore: {
        avgGain: seededInt(1, 10, 23),
        totalGain: seededInt(200, 800, 29),
        improvedPercentage: seededInt(30, 70, 31),
        teamAverage: seededInt(70, 110, 37),
        playerCount: seededInt(20, 120, 59),
        improvedCount: seededInt(10, 80, 61),
      },
      totalArmStrength: {
        avgGain: seededInt(3, 15, 41),
        totalGain: seededInt(400, 1200, 43),
        improvedPercentage: seededInt(30, 70, 47),
        teamAverage: seededInt(110, 180, 53),
        playerCount: seededInt(20, 120, 67),
        improvedCount: seededInt(10, 80, 71),
      },
      testing: {
        totalExams: fresh + post + armPrimer,
        breakdown: { fresh, post, armPrimer },
      },
      training: {
        totalSessions: strength + hybrid + offDay,
        breakdown: { strength, hybrid, offDay },
      },
    };

    return { status: true, data };
  }

  /**
   * Fetches dashboard summary cards from `GET .../summary-cards`.
   *
   * Query params sent: `team`, optional `position` (from playersGroup), `rangeKey` or `startDate`/`endDate`,
   * optional `search`.
   *
   * @param params - Filters; `custom` range uses ISO `startDate`/`endDate` instead of `rangeKey`.
   * @returns `{ status, data }` with normalized {@link DashboardMetricsResponse}; `status: false` on HTTP/error with zeroed-shaped data.
   */
  public async getMetrics(
    params?: DashboardMetricsParams,
  ): Promise<{ status: boolean; data: DashboardMetricsResponse }> {
    if (params?.teamId === NO_DATA_TEAM_ID) {
      return this.getMetricsMock(params);
    }

    const search = new URLSearchParams();
    if (params?.teamId) search.set('team', params.teamId);
    if (params?.search?.trim()) search.set('search', params.search.trim());

    const position = mapPlayersGroupToActivityTablePosition(params?.playersGroup ?? '');
    if (position) search.set('position', position);

    const rangeKey = this.mapSummaryRangeKey(params?.rangePreset);
    if (rangeKey) {
      search.set('rangeKey', rangeKey);
    } else {
      if (params?.startDate) search.set('startDate', params.startDate);
      if (params?.endDate) search.set('endDate', params.endDate);
    }

    const token = this.getAuthToken();

    try {
      const response = await fetch(`${this.url}/v1/organizations/dashboard/summary-cards?${search.toString()}`, {
        method: 'GET',
        /** Avoid HTTP 304 + browser cache reusing a body when query params change (dashboard filters). */
        cache: 'no-store',
        headers: new Headers({
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }),
      });

      if (!response.ok) {
        return { status: false, data: this.normalizeSummaryData(null) };
      }

      const body: any = await response.json();
      return {
        status: true,
        data: this.normalizeSummaryData(body?.data),
      };
    } catch {
      return { status: false, data: this.normalizeSummaryData(null) };
    }
  }

  /**
   * Mock paginated leaderboard for Jest. Sorting: by metric (desc for top/most improved, asc for least), then name.
   * Ranks are 1..n with no gaps. Occasionally returns empty list when not in Jest (seeded random).
   */
  public async getLeaderboardMock(params: {
    metric: DashboardLeaderboardMetricId;
    sort: DashboardLeaderboardSortMode;
    filters: DashboardTableFilterParams;
    limit?: number;
    page?: number;
  }): Promise<{ status: boolean; data: DashboardLeaderboardResponse }> {
    if (!inJest) await sleep(MOCK_API_DELAY_MS);

    const limit = Math.min(params.limit ?? 10, 120);
    const page = Math.max(params.page ?? 1, 1);
    const teams = ['Varsity A', 'Varsity B', 'JV', 'Fall Ball'];
    const positions = ['Pitcher', 'Catcher', 'Infield', 'Outfield', null];

    /** Design reference: top ArmScore list (extend with generated rows if limit > 7). */
    const showcase: DashboardLeaderboardEntry[] = [
      { rank: 0, playerId: 'p-cicero', playerName: 'Chris Cicero', teamId: 't1', teamName: teams[0], metricType: params.metric, metricValue: 137, position: 'Pitcher' },
      { rank: 0, playerId: 'p-danielson', playerName: 'Dane Danielson', teamId: 't1', teamName: teams[0], metricType: params.metric, metricValue: 103, position: 'Pitcher' },
      { rank: 0, playerId: 'p-rivera', playerName: 'Marcus Rivera', teamId: 't1', teamName: teams[0], metricType: params.metric, metricValue: 98, position: 'Infield' },
      { rank: 0, playerId: 'p-thompson', playerName: 'Jake Thompson', teamId: 't2', teamName: teams[1], metricType: params.metric, metricValue: 95, position: 'Outfield' },
      { rank: 0, playerId: 'p-brooks', playerName: 'Tyler Brooks', teamId: 't2', teamName: teams[1], metricType: params.metric, metricValue: 92, position: 'Pitcher' },
      { rank: 0, playerId: 'p-mitchell', playerName: 'Ryan Mitchell', teamId: 't2', teamName: teams[1], metricType: params.metric, metricValue: 89, position: 'Catcher' },
      { rank: 0, playerId: 'p-anderson', playerName: 'Kevin Anderson', teamId: 't3', teamName: teams[2], metricType: params.metric, metricValue: 87, position: 'Infield' },
    ];

    // Deterministic "no rows" when the special empty team is selected.
    if (params.filters.teamId === NO_DATA_TEAM_ID) {
      return { status: true, data: { entries: [] } };
    }

    const seed =
      (params.filters.teamId ?? '') +
      (params.filters.playersGroup ?? '') +
      (params.filters.leaderboardTeamId ?? '') +
      (params.filters.positionFilter ?? '') +
      params.metric +
      params.sort;
    let h = 0;
    for (let i = 0; i < seed.length; i += 1) h = (Math.imul(31, h) + seed.charCodeAt(i)) | 0;
    /** Deterministic PRNG step from seeded `h` for mock row generation. */
    const rand = (n: number) => {
      h = (Math.imul(1103515245, h) + 12345) | 0;
      return Math.abs(h) % n;
    };

    if (!inJest && rand(12) === 0) {
      return { status: true, data: { entries: [] } };
    }

    const firstNames = ['Alex', 'Jordan', 'Sam', 'Taylor', 'Riley', 'Casey', 'Morgan'];
    const lastNames = ['Smith', 'Lee', 'Garcia', 'Brown', 'Davis', 'Wilson', 'Moore'];

    const entries: DashboardLeaderboardEntry[] = showcase.map(e => ({
      ...e,
      metricType: params.metric,
    }));

    while (entries.length < limit + 3) {
      const i = entries.length;
      entries.push({
        rank: 0,
        playerId: `player-mock-${i}-${h}`,
        playerName: `${firstNames[rand(firstNames.length)]} ${lastNames[rand(lastNames.length)]}`,
        teamId: `team-${rand(teams.length)}`,
        teamName: teams[rand(teams.length)],
        metricType: params.metric,
        metricValue: rand(10) === 0 ? null : 60 + rand(50),
        position: positions[rand(positions.length)] ?? null,
      });
    }

    const withValue = entries.filter(e => e.metricValue != null) as (DashboardLeaderboardEntry & {
      metricValue: number;
    })[];
    const withoutValue = entries.filter(e => e.metricValue == null);

    withValue.sort((a, b) => {
      if (params.sort === 'leastImproved') {
        if (a.metricValue !== b.metricValue) return a.metricValue - b.metricValue;
      } else {
        if (a.metricValue !== b.metricValue) return b.metricValue - a.metricValue;
      }
      return a.playerName.localeCompare(b.playerName);
    });

    const ranked = [...withValue, ...withoutValue];
    ranked.forEach((e, idx) => {
      e.rank = idx + 1;
    });

    const start = (page - 1) * limit;
    const paged = ranked.slice(start, start + limit);

    return { status: true, data: { entries: paged, total: ranked.length } };
  }

  /**
   * Fetches leaderboard from `GET .../leaderboard`, maps `data.rows` to {@link DashboardLeaderboardEntry}[].
   *
   * Query: `metricKey`, `sortBy`, `page`, `limit`, date via `rangeKey` or `startDate`/`endDate`,
   * `team`, optional `position` (from `positionFilter` or `playersGroup`), optional `search` (player name).
   *
   * `topScore` uses `bestValue`; improved modes use `change`. Values rounded to 2 decimals.
   */
  public async getLeaderboard(params: {
    metric: DashboardLeaderboardMetricId;
    sort: DashboardLeaderboardSortMode;
    filters: DashboardTableFilterParams;
    limit?: number;
    page?: number;
  }): Promise<{ status: boolean; data: DashboardLeaderboardResponse }> {
    const metricKeyMap: Record<DashboardLeaderboardMetricId, string> = {
      armScore: 'ARM_SCORE',
      totalArmStrength: 'TOTAL_ARM_STRENGTH',
      ir: 'IR_STRENGTH',
      er: 'ER_STRENGTH',
      scaption: 'SCAPTION_STRENGTH',
      grip: 'GRIP_STRENGTH',
      velocity: 'VELOCITY',
      svr: 'SVR',
    };

    const sortByMap: Record<DashboardLeaderboardSortMode, string> = {
      topScore: 'TOP_SCORE',
      mostImproved: 'MOST_IMPROVED',
      leastImproved: 'LEAST_IMPROVED',
    };

    const { metric, sort, filters } = params;
    const limit = Math.min(params.limit ?? 100, 100);
    const page = params.page ?? 1;

    const search = new URLSearchParams();
    search.set('metricKey', metricKeyMap[metric]);
    search.set('sortBy', sortByMap[sort]);
    search.set('limit', String(limit));
    search.set('page', String(page));

    // Range handling: for named presets use rangeKey, for custom pass dates.
    const rangePreset = filters.rangePreset ?? undefined;
    if (rangePreset && rangePreset !== 'custom') {
      search.set('rangeKey', rangePreset === 'all' ? 'ALL' : rangePreset);
    } else {
      if (filters.startDate) search.set('startDate', filters.startDate);
      if (filters.endDate) search.set('endDate', filters.endDate);
    }

    if (filters.teamId) {
      search.set('team', filters.teamId);
    }

    // Position mapping:
    // 1) explicit leaderboard position filter (if selected)
    // 2) fallback to dashboard playersGroup filter
    const positionFilter = (filters.positionFilter ?? '').trim().toLowerCase();
    if (positionFilter === 'all') {
      search.set('position', 'all');
    } else {
      const mappedFromPositionFilter = mapPlayersGroupToActivityTablePosition(positionFilter);
      const mappedFromPlayersGroup = mapPlayersGroupToActivityTablePosition(filters.playersGroup ?? '');
      const mappedPosition = mappedFromPositionFilter ?? mappedFromPlayersGroup;
      if (mappedPosition) {
        search.set('position', mappedPosition);
      }
    }
    if (filters.playerNameSearch?.trim()) {
      search.set('search', filters.playerNameSearch.trim());
    }

    // Auth token from localStorage (same pattern as User API).
    const rawUser = typeof window !== 'undefined' ? window.localStorage.getItem('user') : null;
    const token = rawUser ? (JSON.parse(rawUser) as any)?.access_token : null;

    try {
      const response = await fetch(
        `${this.url}/v1/organizations/dashboard/leaderboard?${search.toString()}`,
        {
          method: 'GET',
          cache: 'no-store',
          headers: new Headers({
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          }),
        },
      );

      if (!response.ok) {
        return { status: false, data: { entries: [] } };
      }

      const body: any = await response.json();
      const data = body?.data ?? {};
      const rows: any[] = data.rows ?? [];

      const entries: DashboardLeaderboardEntry[] = rows.map(row => {
        const player = row.player ?? {};
        const firstName = player.firstName ?? '';
        const lastName = player.lastName ?? '';
        const fullName = `${firstName} ${lastName}`.trim() || 'Unknown player';

        // For TOP_SCORE we display bestValue; for MOST/LEAST_IMPROVED we display delta.
        let metricValue: number | null = null;
        if (sort === 'topScore') {
          metricValue = typeof row.bestValue === 'number' ? row.bestValue : null;
        } else {
          metricValue = typeof row.change === 'number' ? row.change : null;
        }

        // Backend values can have many decimals; clamp to 2 decimals for UI.
        if (metricValue != null) {
          metricValue = Math.round(metricValue * 100) / 100;
        }

        return {
          rank: row.rank ?? 0,
          playerId: player._id ?? '',
          playerName: fullName,
          teamId: '', // Not present in response; keep contract with empty string for now.
          teamName: '',
          metricType: metric,
          metricValue,
          position: player.primaryPosition ?? null,
        };
      });

      return { status: true, data: { entries, total: typeof data.total === 'number' ? data.total : entries.length } };
    } catch {
      return { status: false, data: { entries: [] } };
    }
  }

  /**
   * Fetches paginated activity table from `GET .../activity-table`.
   *
   * Query: `page`, `limit`, optional `team`, `position` (from `playersGroup`), optional `search`,
   * optional `sortBy` + `sortOrder` when column sort is active.
   *
   * @returns Normalized rows via {@link mapActivityTableApiRow} and pagination from response body.
   */
  public async getActivityTable(params: {
    page: number;
    pageSize: number;
    teamId?: string | null;
    playersGroup?: string;
    search?: string;
    sortColumn?: DashboardActivityTableSortColumn | null;
    sortDirection?: DashboardActivityTableSortDirection;
  }): Promise<{ status: boolean; data: DashboardActivityTableResponse }> {
    const limit = Math.min(Math.max(params.pageSize, 1), 100);
    const page = Math.max(params.page, 1);

    if (params.teamId === NO_DATA_TEAM_ID) {
      return {
        status: true,
        data: { rows: [], page, pageSize: limit, totalCount: 0 },
      };
    }

    const search = new URLSearchParams();
    search.set('page', String(page));
    search.set('limit', String(limit));
    if (params.teamId) search.set('team', params.teamId);
    const position = mapPlayersGroupToActivityTablePosition(params.playersGroup ?? '');
    if (position) search.set('position', position);
    if (params.search?.trim()) search.set('search', params.search.trim());
    if (params.sortColumn) {
      search.set('sortBy', ACTIVITY_TABLE_SORT_UI_TO_API[params.sortColumn]);
      search.set('sortOrder', params.sortDirection ?? 'desc');
    }

    const rawUser = typeof window !== 'undefined' ? window.localStorage.getItem('user') : null;
    const token = rawUser ? (JSON.parse(rawUser) as any)?.access_token : null;

    try {
      const response = await fetch(
        `${this.url}/v1/organizations/dashboard/activity-table?${search.toString()}`,
        {
          method: 'GET',
          cache: 'no-store',
          headers: new Headers({
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          }),
        },
      );

      if (!response.ok) {
        return { status: false, data: { rows: [], page, pageSize: limit, totalCount: 0 } };
      }

      const body: any = await response.json();
      const rawRows = Array.isArray(body?.data) ? body.data : [];
      const rows: DashboardActivityRow[] = rawRows.map(mapActivityTableApiRow);
      const pagination = body?.pagination ?? {};
      const totalCount = typeof pagination.total === 'number' ? pagination.total : rows.length;
      const pageSizeResp = typeof pagination.limit === 'number' ? pagination.limit : limit;
      const pageResp = typeof pagination.page === 'number' ? pagination.page : page;

      return {
        status: true,
        data: { rows, page: pageResp, pageSize: pageSizeResp, totalCount },
      };
    } catch {
      return { status: false, data: { rows: [], page, pageSize: limit, totalCount: 0 } };
    }
  }

  /**
   * Mock activity table for Jest: 87 total rows, slice by page. Supports sort via {@link sortDashboardActivityRows}.
   * Same response shape as {@link getActivityTable}.
   */
  public async getActivityTableMock(params: {
    page: number;
    pageSize: number;
    teamId?: string | null;
    playersGroup?: string;
    search?: string;
    sortColumn?: DashboardActivityTableSortColumn | null;
    sortDirection?: DashboardActivityTableSortDirection;
  }): Promise<{ status: boolean; data: DashboardActivityTableResponse }> {
    if (!inJest) await sleep(MOCK_API_DELAY_MS);

    const pageSize = Math.min(Math.max(params.pageSize, 1), 100);
    const page = Math.max(params.page, 1);

    // Deterministic "no rows" when the special empty team is selected.
    if (params.teamId === NO_DATA_TEAM_ID) {
      const pageSize = Math.min(Math.max(params.pageSize, 1), 100);
      const page = Math.max(params.page, 1);
      return {
        status: true,
        data: { rows: [], page, pageSize, totalCount: 0 },
      };
    }

    const seedEmpty =
      JSON.stringify(params.teamId) +
      JSON.stringify(params.playersGroup ?? '') +
      String(pageSize);
    let hEmpty = 0;
    for (let i = 0; i < seedEmpty.length; i += 1)
      hEmpty = (Math.imul(31, hEmpty) + seedEmpty.charCodeAt(i)) | 0;
    /** ~10% chance of empty mock table when not in Jest. */
    const rollEmpty = () => {
      hEmpty = (Math.imul(1103515245, hEmpty) + 12345) | 0;
      return Math.abs(hEmpty) % 10;
    };
    if (!inJest && rollEmpty() === 0) {
      return { status: true, data: { rows: [], page, pageSize, totalCount: 0 } };
    }

    const totalCount = 87;
    const start = (page - 1) * pageSize;

    const mockCtx = { teamId: params.teamId, playersGroup: params.playersGroup };
    let allRows: DashboardActivityRow[] = [];
    for (let idx = 0; idx < totalCount; idx += 1) {
      allRows.push(createMockActivityRow(idx, mockCtx));
    }

    const col = params.sortColumn ?? null;
    const dir = params.sortDirection ?? 'desc';
    if (col) {
      allRows = sortDashboardActivityRows(allRows, col, dir);
    }

    const rows = allRows.slice(start, start + pageSize);

    return {
      status: true,
      data: { rows, page, pageSize, totalCount },
    };
  }

  public async getPlayerSummaryCards(params?: {
    playerId?: string;
    rangeKey?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ status: boolean; data: PlayerSummaryCardsData | null }> {
    const qs = new URLSearchParams();
    if (params?.playerId) qs.set('playerId', params.playerId);
    if (params?.rangeKey) qs.set('rangeKey', params.rangeKey);
    if (params?.startDate) qs.set('startDate', params.startDate);
    if (params?.endDate) qs.set('endDate', params.endDate);

    const token = this.getAuthToken();
    try {
      const res = await fetch(`${this.url}/v1/player/dashboard/summary-cards?${qs}`, {
        method: 'GET',
        cache: 'no-store',
        headers: new Headers({
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }),
      });
      if (!res.ok) return { status: false, data: null };
      const body = await res.json();
      return { status: true, data: body?.data ?? null };
    } catch {
      return { status: false, data: null };
    }
  }

  public async getPlayerWeeklyActivity(weekStart?: string, playerId?: string): Promise<{
    status: boolean;
    data: PlayerWeeklyActivityData | null;
  }> {
    const qs = new URLSearchParams();
    if (playerId) qs.set('playerId', playerId);
    if (weekStart) qs.set('weekStart', weekStart);

    const token = this.getAuthToken();
    try {
      const res = await fetch(`${this.url}/v1/player/dashboard/weekly-activity?${qs}`, {
        method: 'GET',
        cache: 'no-store',
        headers: new Headers({
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        }),
      });
      if (!res.ok) return { status: false, data: null };
      const body = await res.json();
      return { status: true, data: body?.data ?? null };
    } catch {
      return { status: false, data: null };
    }
  }
}

